import configData from "../../config.json";

const ASK_AI_BASE_URL = `${configData.SERVER_API_URL}/askAi`;

function getSavedUserInfo() {
  try {
    return JSON.parse(localStorage.getItem("myprega_user_info") || "{}");
  } catch (e) {
    return {};
  }
}

function normalizeLanguage(language) {
  if (!language) return "Hindi";
  return language.charAt(0).toUpperCase() + language.slice(1);
}

function normalizeTrimester(trimester) {
  const value = `${trimester || ""}`.trim();

  if (value === "1") return "First trimester";
  if (value === "2") return "Second trimester";
  if (value === "3") return "Third trimester";

  return value;
}

function buildPatientInfo(patientInfo) {
  const saved = patientInfo || getSavedUserInfo();
  const payload = {};

  if (saved.name) payload.name = saved.name;
  if (saved.age) payload.age = Number(saved.age);
  if (saved.weight) payload.weight = `${saved.weight} kg`;
  if (saved.trimester) payload.trimester = normalizeTrimester(saved.trimester);
  if (saved.pregnancyWeek) payload.pregnancy_week = Number(saved.pregnancyWeek);
  if (saved.diet !== undefined || saved.veg !== undefined) {
    payload.veg = saved.veg !== undefined ? Boolean(saved.veg) : saved.diet === "true";
  }
  if (saved.allergies) payload.allergies = saved.allergies;

  return payload;
}

function extractAiText(data) {
  if (typeof data === "string") return data;

  return (
    data?.data?.answer ||
    data?.data?.response ||
    data?.data?.text ||
    data?.data?.message ||
    data?.answer ||
    data?.response ||
    data?.text ||
    data?.message ||
    "No response from AI."
  );
}

async function askAi(endpoint, body) {
  const response = await fetch(`${ASK_AI_BASE_URL}/${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data?.message || data?.error || `AI request failed with status ${response.status}`);
  }

  return extractAiText(data);
}

export async function chatWithAI({ text, language, patientInfo }) {
  return askAi("chat", {
    text,
    language: normalizeLanguage(language),
    patient_info: buildPatientInfo(patientInfo),
  });
}

export async function generateDietPlanAI({ patientInfo, language }) {
  return askAi("diet_plan", {
    language: normalizeLanguage(language),
    patient_info: buildPatientInfo(patientInfo),
  });
}

export async function generateYogaPlanAI({ patientInfo, language }) {
  return askAi("yoga_plan", {
    language: normalizeLanguage(language),
    patient_info: buildPatientInfo(patientInfo),
  });
}

export async function explainReportAI({ language, reportText, patientInfo }) {
  return askAi("explain_report", {
    report_text: reportText,
    language: normalizeLanguage(language),
    patient_info: buildPatientInfo(patientInfo),
  });
}

export async function explainReportFileAI({ file, language, patientInfo }) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("language", normalizeLanguage(language));
  formData.append("patient_info", JSON.stringify(buildPatientInfo(patientInfo)));

  const response = await fetch(`${ASK_AI_BASE_URL}/explain_report_file`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data?.message || data?.error || `Report upload failed with status ${response.status}`);
  }

  return extractAiText(data);
}
